import type { ThemeColor } from '@vrplatform/theme';
import {
  Center,
  Badge as MBadge,
  type MantineSize,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type { EmotionSx as Sx } from '@mantine/emotion';
import {
  type ComponentProps,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
} from 'react';

type ButtonType = {
  component: 'button';
  type?: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};

type BadgeType = {
  component?: 'div';
  type?: undefined;
  disabled?: undefined;
  onClick?: undefined;
};

type Props = {
  color?: ThemeColor;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: MantineSize;
  sx?: Sx;
  variant?: ComponentProps<typeof MBadge<'div'>>['variant'];
  tooltip?: ReactNode;
} & (ButtonType | BadgeType);

export const Badge = ({
  color: c,
  disabled,
  leftIcon,
  rightIcon,
  children,
  component = 'div',
  size = 'md',
  sx = {},
  onClick,
  variant,
  tooltip,
}: PropsWithChildren<Props>) => {
  const { colorScheme } = useMantineColorScheme();
  const { colors, primaryColor, white } = useMantineTheme();
  const color = c || primaryColor;

  const isDarkTheme = colorScheme === 'dark';

  const { backgroundColor, borderColor, textColor } = useMemo(() => {
    return {
      textColor: (() => {
        if (variant === 'white') return colors.neutral[8];

        return colors[color][isDarkTheme ? 4 : 9];
      })(),
      backgroundColor: (() => {
        if (variant === 'white') return white;

        return isDarkTheme ? undefined : `${colors[color][1]}80!important`;
      })(),
      borderColor: (() => {
        if (variant === 'white') return colors.neutral[3];

        return colors[color][isDarkTheme ? 1 : 2] + 60;
      })(),
    };
  }, [color, variant, isDarkTheme, colors, white]);

  return (
    <Tooltip disabled={!tooltip} label={tooltip} withArrow maw={400} multiline>
      <MBadge
        component={component}
        disabled={disabled}
        onClick={onClick as any}
        color={color}
        variant={variant}
        size={size}
        sx={(theme, u) =>
          Object.assign(
            {
              cursor: disabled || !onClick ? undefined : 'pointer',
              paddingInline: rem(5),
              fontWeight: 500,
              textTransform: 'none',
              color: textColor,
              ':active':
                component === 'button'
                  ? {
                      transform: 'translateY(1px)',
                    }
                  : {},
            },
            typeof sx === 'function' ? sx(theme, u) : sx
          )
        }
        styles={{
          root: {
            backgroundColor,
            borderColor,
          },
        }}
        leftSection={leftIcon && <Center>{leftIcon}</Center>}
        rightSection={rightIcon && <Center>{rightIcon}</Center>}
      >
        {children}
      </MBadge>
    </Tooltip>
  );
};
